import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";

type PhpExtension = {
  id: string;
  name: string;
  shortName?: string;
  enabled: boolean;
  available?: boolean;
  zend?: boolean;
  note?: string;
};

type PhpVersionOpt = {
  version: string;
  available?: boolean;
  default?: boolean;
  binary?: string | null;
};

/**
 * Softaculous / Webuzo-style PHP Extensions manager.
 * Version selector + multi-column .so checkbox grid + batch save.
 */
export function PhpModulesPage() {
  const [items, setItems] = useState<PhpExtension[]>([]);
  const [versions, setVersions] = useState<PhpVersionOpt[]>([]);
  const [version, setVersion] = useState("");
  const [enabledCount, setEnabledCount] = useState(0);
  const [disabledCount, setDisabledCount] = useState(0);
  const [docsNote, setDocsNote] = useState("");
  const [note, setNote] = useState("");
  const [hasPhp, setHasPhp] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDocs, setShowDocs] = useState(true);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");

  const load = useCallback(async (ver?: string) => {
    setError("");
    try {
      const q = ver ? `?version=${encodeURIComponent(ver)}` : "";
      const res = await api<{
        items: PhpExtension[];
        versions?: PhpVersionOpt[];
        version?: string;
        enabledCount?: number;
        disabledCount?: number;
        docsNote?: string;
        note?: string;
        hasPhp?: boolean;
      }>(`/api/v1/php/modules${q}`);
      setItems(res.items || []);
      setVersions(res.versions || []);
      setVersion(res.version || ver || "");
      setEnabledCount(res.enabledCount ?? 0);
      setDisabledCount(res.disabledCount ?? 0);
      setDocsNote(
        res.docsNote ||
          "You can find custom docs for PHP extensions here: https://www.php.net/manual/en/extensions.membership.php"
      );
      setNote(res.note || "");
      setHasPhp(Boolean(res.hasPhp));
      setPending({});
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const display = useMemo(() => {
    let list = items.map((m) => ({
      ...m,
      enabled: pending[m.id] !== undefined ? pending[m.id] : m.enabled
    }));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.id.toLowerCase().includes(q) ||
          (m.shortName || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, pending, search]);

  const liveEnabled = display.filter((m) => m.enabled).length;
  const liveDisabled = display.filter((m) => !m.enabled).length;
  const allSelected = display.length > 0 && display.every((m) => m.enabled);
  const dirty = Object.keys(pending).length > 0;

  const toggleOne = (id: string, enabled: boolean) => {
    setPending((p) => ({ ...p, [id]: enabled }));
  };

  const selectAll = (enabled: boolean) => {
    const next: Record<string, boolean> = { ...pending };
    for (const m of display) next[m.id] = enabled;
    setPending(next);
  };

  const onVersionChange = (v: string) => {
    if (dirty) {
      if (
        !confirm(
          "You have unsaved extension changes. Switch PHP version and discard them?"
        )
      ) {
        return;
      }
    }
    setVersion(v);
    void load(v);
  };

  const applyChanges = async () => {
    const modules = Object.entries(pending).map(([id, enabled]) => ({
      id,
      enabled
    }));
    if (!modules.length) {
      setMsg("No changes to save");
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/php/modules", {
        method: "PUT",
        body: JSON.stringify({ version, modules })
      });
      setMsg(
        `Updated ${modules.length} extension(s) for PHP ${version || "default"}`
      );
      await load(version || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const docsHref =
    "https://www.php.net/manual/en/extensions.membership.php";

  return (
    <>
      <div className="topbar">
        <div>
          <h1 className="php-ext-title">
            <span className="php-ext-icon" aria-hidden>
              🐘
            </span>
            PHP Extensions
          </h1>
          <p>Enable or disable PHP extensions per version (Softaculous-style)</p>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load(version || undefined)}
            disabled={busy}
          >
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="php-ext-toolbar">
        <label className="php-ext-version-label">
          Select PHP Version :
          <select
            value={version}
            onChange={(e) => onVersionChange(e.target.value)}
            disabled={busy}
          >
            {versions.length === 0 ? (
              <option value={version || ""}>{version || "—"}</option>
            ) : (
              versions.map((v) => (
                <option key={v.version} value={v.version}>
                  {v.version}
                  {v.default ? " (default)" : ""}
                  {v.available === false ? " — n/a" : ""}
                </option>
              ))
            )}
          </select>
        </label>
        <input
          type="search"
          className="php-ext-search-input"
          placeholder="Filter extensions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {showDocs ? (
        <div className="php-ext-banner php-ext-docs">
          <div>
            <strong>Note:</strong> You can find custom docs for PHP extensions
            here:{" "}
            <a href={docsHref} target="_blank" rel="noreferrer">
              {docsHref}
            </a>
            {docsNote && !docsNote.includes("php.net") ? (
              <span> — {docsNote}</span>
            ) : null}
          </div>
          <button
            type="button"
            className="php-ext-dismiss"
            onClick={() => setShowDocs(false)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ) : null}

      {!hasPhp ? (
        <div className="card full" style={{ marginBottom: 12 }}>
          <p className="card-meta faint">
            {note ||
              "PHP not fully detected on this host — preferences are stored and applied when PHP packages are available."}
          </p>
        </div>
      ) : null}

      <div className="php-ext-panel">
        <div className="php-ext-panel-head">
          <span>
            Extensions [ Enabled: {dirty ? liveEnabled : enabledCount},
            Disabled: {dirty ? liveDisabled : disabledCount} ]
          </span>
          <label className="php-ext-select-all">
            Select All
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => selectAll(e.target.checked)}
              disabled={busy || display.length === 0}
            />
          </label>
        </div>

        <div className="php-ext-grid">
          {display.length === 0 ? (
            <div className="php-ext-empty">
              {items.length === 0
                ? "No PHP extensions found for this version."
                : "No extensions match the filter."}
            </div>
          ) : (
            display.map((m) => (
              <label
                key={m.id}
                className={`php-ext-item${m.available === false ? " is-unavailable" : ""}`}
                title={m.note || m.shortName || m.id}
              >
                <input
                  type="checkbox"
                  checked={m.enabled}
                  onChange={(e) => toggleOne(m.id, e.target.checked)}
                  disabled={busy}
                />
                <span className="mono">{m.name}</span>
              </label>
            ))
          )}
        </div>

        <div className="php-ext-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !dirty}
            onClick={() => void applyChanges()}
          >
            {busy ? "Saving…" : "Save Settings"}
          </button>
          {dirty ? (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => setPending({})}
            >
              Discard changes
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}
